import { ResponsesGetReportingFilesByFilterResponse } from '@genial/services/dist/models';
import { Utils } from '@genial/utils';

import { getValidatedStatusIconAsSingleIconStatus } from '../../utils/getValidatedStatusIcon';

import {
  findReportGeneratorCategory,
  findReportGeneratorDocumentType,
  findReportGeneratorSubCategory,
  formatReportGeneratorTypeLabel,
  ReportGeneratorCategory,
} from './categories';
import {
  buildReportGeneratorDetailParameters,
  formatSelectedPeriodDisplay,
  getDocumentTypeSecondaryText,
} from './displayParameters';
import { getReportingFilePeriodLabel } from './periods';
import { ReportGeneratorListItemNormalized } from './types';

const STATUS_ID_DOWNLOAD_ENABLED = 200;
const STATUS_ID_ERROR = 500;
const MAX_RETRY_ATTEMPTS = 3;

export const normalizeReportGeneratorListItem = (
  { reportingFileItem }: ResponsesGetReportingFilesByFilterResponse,
  categories: ReportGeneratorCategory[] = []
): ReportGeneratorListItemNormalized => {
  const statusIcon = getValidatedStatusIconAsSingleIconStatus(
    reportingFileItem.status,
    'statusIcon'
  );

  const statusId = Number(reportingFileItem.status?.statusId);
  const retryAttempt = Number(reportingFileItem.retryAttempt ?? 0);
  const category = findReportGeneratorCategory(categories, reportingFileItem.categoryId);
  const catalogDocumentType = findReportGeneratorDocumentType(
    categories,
    `${reportingFileItem.documentTypeId}`
  );
  const documentTypeLabel =
    catalogDocumentType?.label?.trim() || reportingFileItem.documentType?.trim() || '--';
  const subCategory =
    findReportGeneratorSubCategory(
      categories,
      reportingFileItem.subCategoryId != null ? `${reportingFileItem.subCategoryId}` : undefined
    ) ?? findReportGeneratorSubCategory(categories, catalogDocumentType?.subCategoryId);
  const fileName = reportingFileItem.reportingFile?.excelFileName?.trim() || undefined;
  const catalogDefinesPeriod = Boolean(catalogDocumentType?.periodOptions.length);
  const selectedPeriodDisplay = catalogDefinesPeriod
    ? formatSelectedPeriodDisplay(reportingFileItem)
    : '--';

  return {
    ...reportingFileItem,
    id: reportingFileItem.reportingId,
    documentType: formatReportGeneratorTypeLabel(category?.label, documentTypeLabel),
    documentTypeLabel,
    categoryLabel: category?.label?.trim() || '--',
    subCategoryLabel: subCategory?.label?.trim() || '--',
    periodTypeLabel: catalogDefinesPeriod
      ? getReportingFilePeriodLabel(reportingFileItem.reportingPeriod)
      : '--',
    documentTypeSecondaryText: getDocumentTypeSecondaryText(reportingFileItem, {
      includePeriodLabel: catalogDefinesPeriod,
    }),
    requestedAtNormalized: reportingFileItem.createdAt
      ? Utils.Mask.DateFormat(reportingFileItem.createdAt, 'dd/MM/yyyy')
      : '--',
    requestedAtDetailed: reportingFileItem.createdAt
      ? Utils.Mask.DateFormat(reportingFileItem.createdAt, 'dd/MM/yyyy HH:mm')
      : '--',
    fileName,
    detailParameters: buildReportGeneratorDetailParameters(reportingFileItem, catalogDocumentType),
    selectedPeriodDisplay,
    status: reportingFileItem.status.statusAlias || '--',
    statusIcon,
    normalizedError: reportingFileItem.reportingFileError?.normalizedMessage?.trim() || undefined,
    isDownloadEnabled: statusId === STATUS_ID_DOWNLOAD_ENABLED,
    isFileWithError: statusId === STATUS_ID_ERROR,
    isAbleToRetry: statusId === STATUS_ID_ERROR && retryAttempt < MAX_RETRY_ATTEMPTS,
  };
};

export const normalizeReportGeneratorList = (
  response: ResponsesGetReportingFilesByFilterResponse[] | undefined | null,
  categories: ReportGeneratorCategory[] = []
): ReportGeneratorListItemNormalized[] =>
  response?.map((item) => normalizeReportGeneratorListItem(item, categories)) || [];
